class Websockets {

    constructor(enabled = true) {
        this.socket = null;
        this.debug = false;
        this.reconnectAttempts = 0;
        this.maxReconnectAttempts = 50;
        this.initialReconnectInterval = 2000;
        this.maxReconnectInterval = 30000;
        this.connectTimeoutMs = 15000;
        this.loginTimeoutMs = 15000;
        this.statusIntervalMs = 30000;
        this.ready = false;
        this.enabled = enabled;
        this.allowReconnect = enabled;
        this.statusInterval = null;
        this.connectTimeout = null;
        this.loginTimeout = null;
        this.reconnectTimer = null;

        this.callbacks = {
            message: [],
            open: [],
            close: [],
            error: [],
            reconnect: []
        };

        if (this.enabled) {
            this.connect();
        }
    }

    connect() {
        if (!this.enabled || !this.allowReconnect) {
            this.debugLog('WebSocket disabled - pomijam połączenie');
            return;
        }

        if (this.socket && [WebSocket.CONNECTING, WebSocket.OPEN].includes(this.socket.readyState)) {
            this.debugLog('WebSocket już jest połączony lub w trakcie łączenia');
            return;
        }

        this.clearReconnectTimer();
        this.clearConnectTimeout();
        this.clearLoginTimeout();
        this.clearStatusInterval();
        this.ready = false;

        try {
            const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
            const host = window.location.host;
            const wsUrl = `${protocol}//${host}/ws`;

            if (host.startsWith('127.0.0.1')) {
                this.socket = new WebSocket('ws://127.0.0.1:9502');
            } else {
                this.socket = new WebSocket(wsUrl);
            }

            this.connectTimeout = window.setTimeout(() => {
                if (this.socket && this.socket.readyState === WebSocket.CONNECTING) {
                    this.debugLog('Przekroczono czas oczekiwania na połączenie WebSocket');
                    this.socket.close(4000, 'Connection timeout');
                }
            }, this.connectTimeoutMs);

            this.socket.addEventListener('open', async (event) => {
                this.debugLog('Połączenie z WebSocket zostało nawiązane');
                this.clearConnectTimeout();
                this.clearLoginTimeout();

                this.loginTimeout = window.setTimeout(() => {
                    if (this.socket && this.socket.readyState === WebSocket.OPEN && !this.ready) {
                        this.debugLog('Przekroczono czas oczekiwania na autoryzację WebSocket');
                        this.socket.close(4001, 'Login timeout');
                    }
                }, this.loginTimeoutMs);

                try {
                    const getTokenResponse = await this.getToken();

                    if (this.socket?.readyState !== WebSocket.OPEN) {
                        return;
                    }

                    if (getTokenResponse?.token) {
                        this.socket.send(JSON.stringify({
                            type: 'login',
                            token: getTokenResponse.token
                        }));
                        this.debugLog('Token wysłany do serwera');
                    } else {
                        this.disableAuth('Brak tokena autoryzacyjnego');
                    }
                } catch (error) {
                    this.debugLog('Nie udało się pobrać tokena WebSocket', error);
                    this.disableAuth('Brak tokena autoryzacyjnego');
                }
            });

            this.socket.addEventListener('message', (event) => {
                this.debugLog('Otrzymano wiadomość:', event.data);

                try {
                    const data = JSON.parse(event.data);

                    if (data.type === 'login' && data.status === 'failed') {
                        window.taskello?.showToast?.('Błąd połączenia z serwerem', 'error');
                        this.disableAuth('Brak tokena autoryzacyjnego');
                    } else if (data.type === 'login' && data.status === 'success') {
                        this.ready = true;
                        this.allowReconnect = true;
                        this.reconnectAttempts = 0;
                        this.clearLoginTimeout();
                        this.clearStatusInterval();

                        this.statusInterval = window.setInterval(() => {
                            this.send(JSON.stringify({ status: 'success', type: 'status', action: 'update' }));
                        }, this.statusIntervalMs);

                        this.callbacks.open.forEach(callback => {
                            try {
                                callback(event, this.socket, this);
                            } catch (error) {
                                this.debugLog('Błąd w callback open:', error);
                            }
                        });
                    }

                    if (data.type === 'toast' && data.status === 'failed') {
                        window.taskello?.showToast?.(data.message, 'error');
                    }
                } catch (error) {
                    this.debugLog('Błąd odczytu json', error);
                }

                this.callbacks.message.forEach(callback => {
                    try {
                        callback(event.data, this.socket, this, event);
                    } catch (error) {
                        this.debugLog('Błąd w callback message:', error);
                    }
                });
            });

            this.socket.addEventListener('close', (event) => {
                this.debugLog('Połączenie zamknięte:', event.code, event.reason);
                this.ready = false;
                this.clearConnectTimeout();
                this.clearLoginTimeout();
                this.clearStatusInterval();

                this.callbacks.close.forEach(callback => {
                    try {
                        callback(event, this.socket, this);
                    } catch (error) {
                        this.debugLog('Błąd w callback close:', error);
                    }
                });

                this.handleReconnect();
            });

            this.socket.addEventListener('error', (event) => {
                this.debugLog('Błąd WebSocket:', event);

                this.callbacks.error.forEach(callback => {
                    try {
                        callback(event, this.socket, this);
                    } catch (error) {
                        this.debugLog('Błąd w callback error:', error);
                    }
                });

                if (this.socket && [WebSocket.CONNECTING, WebSocket.OPEN].includes(this.socket.readyState)) {
                    this.socket.close();
                }
            });

        } catch (error) {
            this.debugLog('Błąd przy tworzeniu WebSocket:', error);
            this.handleReconnect();
        }
    }

    handleReconnect() {
        if (!this.enabled || !this.allowReconnect) {
            this.debugLog('Reconnect disabled - pomijam ponowne połączenie');
            return;
        }

        if (this.reconnectTimer) {
            return;
        }

        if (this.reconnectAttempts < this.maxReconnectAttempts) {
            this.reconnectAttempts++;
            const reconnectDelay = Math.min(
                this.initialReconnectInterval * (2 ** (this.reconnectAttempts - 1)),
                this.maxReconnectInterval
            );

            this.debugLog(`Próba ponownego połączenia ${this.reconnectAttempts}/${this.maxReconnectAttempts} za ${reconnectDelay}ms`);

            this.callbacks.reconnect.forEach(callback => {
                try {
                    callback(this.reconnectAttempts, this.maxReconnectAttempts, this);
                } catch (error) {
                    this.debugLog('Błąd w callback reconnect:', error);
                }
            });

            this.reconnectTimer = window.setTimeout(() => {
                this.reconnectTimer = null;
                this.connect();
            }, reconnectDelay);
        } else {
            this.debugLog('Maksymalna liczba prób połączenia została przekroczona');
        }
    }

    disableAuth(reason = 'Brak autoryzacji') {
        this.allowReconnect = false;
        this.ready = false;
        this.clearReconnectTimer();
        this.clearConnectTimeout();
        this.clearLoginTimeout();
        this.clearStatusInterval();

        this.debugLog('Wyłączanie WebSocket z powodu braku autoryzacji:', reason);

        if (this.socket && [WebSocket.CONNECTING, WebSocket.OPEN].includes(this.socket.readyState)) {
            this.socket.close(1000, reason);
        }
    }

    received(callback) {
        return this.onMessage(callback);
    }

    onMessage(callback) {
        if (typeof callback === 'function') {
            this.callbacks.message.push(callback);
        }

        return this;
    }

    onOpen(callback) {
        if (typeof callback === 'function') {
            this.callbacks.open.push(callback);
        }

        return this;
    }

    onClose(callback) {
        if (typeof callback === 'function') {
            this.callbacks.close.push(callback);
        }

        return this;
    }

    onError(callback) {
        if (typeof callback === 'function') {
            this.callbacks.error.push(callback);
        }

        return this;
    }

    onReconnect(callback) {
        if (typeof callback === 'function') {
            this.callbacks.reconnect.push(callback);
        }

        return this;
    }

    // Metody do usuwania callback-ów
    removeCallback(eventType, callback) {
        if (this.callbacks[eventType]) {
            const index = this.callbacks[eventType].indexOf(callback);

            if (index > -1) {
                this.callbacks[eventType].splice(index, 1);
            }
        }

        return this;
    }

    removeAllCallbacks(eventType = null) {
        if (eventType && this.callbacks[eventType]) {
            this.callbacks[eventType] = [];
        } else if (!eventType) {
            Object.keys(this.callbacks).forEach(key => {
                this.callbacks[key] = [];
            });
        }

        return this;
    }

    send(message) {
        if (this.socket && this.socket.readyState === WebSocket.OPEN) {
            this.socket.send(message);
            this.debugLog('Wysłano wiadomość:', message);

            return true;
        } else {
            this.debugLog('WebSocket nie jest połączony. Stan:', this.socket?.readyState);

            return false;
        }
    }

    isConnected() {
        return !!(this.socket && this.socket.readyState === WebSocket.OPEN);
    }

    isReady() {
        return this.ready && this.isConnected();
    }

    getReadyState() {
        return this.socket ? this.socket.readyState : null;
    }

    getReadyStateText() {
        if (!this.socket) return 'NO_SOCKET';

        const states = {
            [WebSocket.CONNECTING]: 'CONNECTING',
            [WebSocket.OPEN]: 'OPEN',
            [WebSocket.CLOSING]: 'CLOSING',
            [WebSocket.CLOSED]: 'CLOSED'
        };

        return states[this.socket.readyState] || 'UNKNOWN';
    }

    disconnect() {
        this.allowReconnect = false;
        this.ready = false;
        this.clearReconnectTimer();
        this.clearConnectTimeout();
        this.clearLoginTimeout();
        this.clearStatusInterval();

        if (this.socket) {
            this.socket.close();
        }

        return this;
    }

    clearStatusInterval() {
        if (this.statusInterval) {
            clearInterval(this.statusInterval);
            this.statusInterval = null;
        }
    }

    clearConnectTimeout() {
        if (this.connectTimeout) {
            clearTimeout(this.connectTimeout);
            this.connectTimeout = null;
        }
    }

    clearLoginTimeout() {
        if (this.loginTimeout) {
            clearTimeout(this.loginTimeout);
            this.loginTimeout = null;
        }
    }

    clearReconnectTimer() {
        if (this.reconnectTimer) {
            clearTimeout(this.reconnectTimer);
            this.reconnectTimer = null;
        }
    }

    setDebug(enabled) {
        this.debug = enabled;
        return this;
    }

    async getToken() {
        return await $.get('/accounts/getWebsocketToken');
    }

    debugLog(...data) {
        if (this.debug) {
            console.log('WebSocket debug:', ...data);
        }
    }
}

const websocket = new Websockets(Boolean(window.Taskello?.config?.isAuth));

window.Websockets = Websockets;
window.websocket = websocket;
window.taskello = window.taskello || {};
window.taskello.websocket = websocket;
